/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class UtilityProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	// TODO: This may be eventually replaced with the HeadlightRestClient/OfflineRestClient
	/**
	 * @param {object} options - JSON containing all the configurations for this query (including filters, sorting, etc.)
	 * @return {string} a meadow filter query stanza created from 'options' (e.g FBV~Something~EQ~Less~FSF~Else~ASC~0/0/250)
	 */
	generateMeadowFilter(options)
	{
		const queryFilter = [];
		// add filter stanzas
		for (const entry of (options.filters?? []))
		{
			const filterPrefix = entry.connector === 'OR' ? 'FBVOR' : 'FBV';
			switch (entry.comparator)
			{
			case 'FOP':
				queryFilter.push('FOP~0~(~0');
				break;
			case 'FCP':
				queryFilter.push('FCP~0~)~0');
				break;
			case 'INN':
			case 'NIN':
				const listPrefix = entry.connector === 'OR' ? 'FBLOR' : 'FBL';
				let inList = Array.isArray(entry.target) ? entry.target : [ entry.target ];
				inList = inList.map((singleTarget) => encodeURIComponent(singleTarget));
				queryFilter.push(`${listPrefix}~${entry.field}~${entry.comparator}~${inList}`);
				break;
			case 'LK':
				queryFilter.push(`${filterPrefix}~${entry.field}~LK~%25${encodeURIComponent(entry.target.replace(/\*/g, '%25'))}%25`);
				break;
			default:
				queryFilter.push(`${filterPrefix}~${entry.field}~${entry.comparator}~${encodeURIComponent(entry.target)}`);
				break;
			}
		}
		// add sorting
		if (options.hasOwnProperty('sort') && options.sort.field && options.sort.direction)
		{
			queryFilter.push(`FSF~${options.sort.field}~${options.sort.direction}~0`);
		}
		const filterString = queryFilter.join('~');
		// NOTED: Removed pagination due to issues with /Count/ fetching
		// add pagination
		// if (options.hasOwnProperty('pagination'))
		// {
		// 	filterString += `/${options.pagination.start}/${options.pagination.limit}`;
		// }
		const hasFilters = (queryFilter.length > 0) || (options.sort && options.sort?.field && options.sort?.direction);
		return (hasFilters ? 'FilteredTo/' : '') + filterString;
	}

	/**
	 * This method formats the results for the Select2 AJAX call.
	 * @param {Array} pData - The data to format.
	 * @param {string|array} pIDKey - The key(s) to use for the ID.
	 * @param {string|array} pTextKey - The key(s) to use for the text.
	 * @param {Array} pSelectedOptions - The selected options to add to the top of the list.
	 * @param {Object} pStickyOption - The sticky option to add to the top of the list.
	 * @param {Boolean} [joinTextKeys] - Should the text key array be joined together.
	 * @return {Array} The formatted results.
	 */
	formatSelect2Results(pData, pIDKey, pTextKey, pSelectedOptions, pStickyOption, joinTextKeys)
	{
		let results = $.map(pData, function(item)
		{
			const result = {};
			// if they pIDKey is an array, we need to loop through the array keys as fallback values
			if (Array.isArray(pIDKey))
			{
				for (let i = 0; i < pIDKey.length; i++)
				{
					if (item[pIDKey[i]])
					{
						result.id = item[pIDKey[i]];
						// if we have selected options, mark them as selected in the dropdown list
						if (pSelectedOptions && pSelectedOptions.includes(result.id))
						{
							result.selected = true;
						}
						break;
					}
				}
			}
			else
			{
				result.id = item?.[pIDKey] || item?.Hash || 0;
			}
			// if they pTextKey is an array, we need to loop through the array keys as fallback values
			if (Array.isArray(pTextKey))
			{
				for (let i = 0; i < pTextKey.length; i++)
				{
					if (item[pTextKey[i]])
					{
						result.text = joinTextKeys ? result.text ? (result.text + ' ' + item[pTextKey[i]]) : item[pTextKey[i]] : item[pTextKey[i]];
						if (!joinTextKeys)
						{
							break;
						}
					}
				}
			}
			else
			{
				result.text = item?.[pTextKey] || item?.Value || ' ';
			}
			return result;
		});
		// go ahead and sort by the text value before returning
		results.sort((a, b) => (a.text > b.text) ? 1 : -1);
		// if we have a sticky option, add it to the top of the dropdown list
		if (pStickyOption)
		{
			// set the sticky flag so we can style it differently in the dropdown
			pStickyOption.sticky = true;
			// remove the sticky option from the results if it already exists
			results = results.filter((item) => item.id !== pStickyOption.id);
			// then, add it to the top of the list
			results.unshift(pStickyOption);
		}
		return results;
	}

	/**
	 * Clear the date time picker.
	 * @param {string} pInputHTMLID The input HTML ID.
	 * @return {void}
	 */
	clearDateTimePicker(pInputHTMLID)
	{
		const tmpFlatpickrElement = document.querySelector(pInputHTMLID);
		if (tmpFlatpickrElement)
		{
			const tmpFlatpickrInstance = tmpFlatpickrElement['_flatpickr'];
			if (tmpFlatpickrInstance)
			{
				tmpFlatpickrInstance.clear();
				// create a new change event to trigger the data request
				const tmpChangeEvent = new Event('change', { bubbles: true });
				tmpFlatpickrElement.dispatchEvent(tmpChangeEvent);
				tmpFlatpickrInstance.close();
			}
		}
	}

	/**
	 * Set the date time picker to the 'now' date/time.
	 * @param {string} pInputHTMLID The input HTML ID.
	 * @return {void}
	 */
	setDateTimePickerToNow(pInputHTMLID)
	{
		const tmpFlatpickrElement = document.querySelector(pInputHTMLID);
		if (tmpFlatpickrElement)
		{
			const tmpFlatpickrInstance = tmpFlatpickrElement['_flatpickr'];
			if (tmpFlatpickrInstance)
			{
				tmpFlatpickrInstance.setDate(new Date());
				// create a new change event to trigger the data request
				const tmpChangeEvent = new Event('change', { bubbles: true });
				tmpFlatpickrElement.dispatchEvent(tmpChangeEvent);
				tmpFlatpickrInstance.close();
			}
		}
	}

	/**
	 * This method builds the extra buttons for the flatpickr date picker.
	 * @param {Object} pInstance - The flatpickr instance.
	 * @param {string} pElementID - The ID of the date picker input element.
	 * @return {void}
	 */
	buildFlatpickrExtraButtons(pInstance, pElementID)
	{
		const template =
		`<div class="flatpicker-extras">
			<div class="buttons py-1 is-centered">
				<button class="button is-outline is-small" type="button" onclick="_Pict.providers.UtilityProvider.clearDateTimePicker('#${pElementID}')">
					<span class="icon">
						<i class="fa fa-fw fa-undo"></i>
					</span>
					<span class="icon-text">
						Clear
					</span>
				</button>
				<button class="button is-link is-small " type="button" onclick="_Pict.providers.UtilityProvider.setDateTimePickerToNow('#${pElementID}')">
					<span class="icon">
						<i class="fa fa-fw fa-calendar"></i>
					</span>
					<span class="icon-text">
						Set to Current Date
					</span>
				</button>
			</div>
		</div>`;
		// add the extra buttons to the calendar container
		if (!pInstance.calendarContainer)
		{
			console.warn('[Utility] Unable to add flatpickr extra buttons, calendarContainer is not defined.');
			return;
		}
		pInstance.calendarContainer.insertAdjacentHTML('beforeend', template);
	}

	/**
	 * This method is used to validate if a passed string is a valid latitude geocoordinate.
	 * The value is not altered, but it throws out a warning in the console for invalid values.
	 * @param {string} value - the geolocation string
	 * @return {string} - the same, unaltered geolocation string
	 * @example
	 * IsValidLatitude('24.034345') => '24.034345'
	 */
	isValidLatitude = (value) =>
	{
		const latReg = RegExp(/^(\+|-)?(?:90(?:(?:\.0{0,16})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{0,16})?))$/);
		const pass = latReg.test(value);
		if (!pass)
		{
			console.warn(`[Utility] This is not a valid Latitude coordinate:`, value);
			return '';
		}
		return value;
	};

	/**
	 * This method is used to validate if a passed string is a valid longitue geocoordinate.
	 * The value is not altered, but it throws out a warning in the console for invalid values.
	 * @param {string} value - the geolocation string
	 * @return {string} - the same, unaltered geolocation string
	 * @example
	 * IsValidLongitude('24.034345') => '24.034345'
	 */
	isValidLongitude = (value) =>
	{
		const longReg = RegExp(/^(\+|-)?(?:180(?:(?:\.0{0,16})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{0,16})?))$/);
		const pass = longReg.test(value);
		if (!pass)
		{
			console.warn(`[Utility] This is not a valid Longitude coordinate:`, value);
			return '';
		}
		return value;
	};

	/**
	 * Get a Google Maps URL for a the passed entity record.
	 * @param {Object} pEntity - The record to get the Google Maps URL for (this assume the record has a User/Capture latitude and longitude)
	 * @param {Object} pOptions - The options for the Google Maps URL
	 * @return {string} - The Google Maps URL
	 */
	getGoogleMapURLFromEntityRecord(pEntity, pOptions = {})
	{
		// Documentation for Google Maps = https://developers.google.com/maps/documentation/maps-static/dev-guide
		let staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap`;
		const tmpMarkerColor = pOptions.markerColor || 'orange';
		// map params for google maps
		const urlParams = {
			scale: pOptions.scale || 1,
			size: pOptions.size || '640x480',
			key: pOptions.key || 'AIzaSyD4ZhOfhsWIII7qOYKRS2SfKZ1iPeIeQfU',
			maptype: pOptions.maptype || 'roadmap',
		};
		// build url from params
		Object.entries(urlParams).forEach(([ key, val ]) =>
		{
			staticMapUrl += `${ key === 'scale' ? '?' : '&' }${ key }=${ val }`;
		});
		// TODO: make this better for "0.000" strings, etc
		const tmpLatitude = pEntity.Latitude || pEntity.CaptureLatitude || pEntity.UserLatitude || 0;
		const tmpLongitude = pEntity.Longitude || pEntity.CaptureLongitude || pEntity.UserLongitude || 0;

		let hasValidGeolocation = false;
		if (tmpLatitude && tmpLongitude)
		{
			hasValidGeolocation = this.isValidLatitude(tmpLatitude) && this.isValidLongitude(tmpLongitude) && (Number(tmpLatitude) !== 0 && Number(tmpLongitude) !== 0);
		}

		if (hasValidGeolocation)
		{
			staticMapUrl += `&markers=color:${ tmpMarkerColor }%7Csize:tiny%7C${ tmpLatitude },${ tmpLongitude }`;
		}
		else
		{
			console.warn(`[Utility] Unable to get Google Maps URL for entity record:`, pEntity);
			return '';
		}
		return staticMapUrl;
	};

	/**
	 * Remove all 'is-loading' classes from the document.
	 * @return {void}
	 */
	removeAllLoadingClasses()
	{
		document.querySelectorAll('.is-loading').forEach((element) =>
		{
			element.classList.remove('is-loading');
		});
	}

	/**
	 * This method generates a HSL color based on the passed string.
	 * @param {string} str - The string to generate the color from.
	 * @param {number} s - The saturation of the color (0-100).
	 * @param {number} l - The lightness of the color (0-100).
	 * @return {string} - The HSL color.
	 */
	stringToHSLColor(str, s, l)
	{
		let hash = 0;
		for (let i = 0; i < str.length; i++)
		{
			hash = str.charCodeAt(i) + ((hash << 5) - hash);
		}

		const h = hash % 360;
		return 'hsl('+h+', '+s+'%, '+l+'%)';
	}

	/**
	 * This method highlights a list item row element by adding a class and scrolling it into view.
	 * @param {HTMLElement} pListItemRowElement - The list item row element to highlight.
	 * @param {string} pScrollConainerElement - The scroll container element to scroll into view (optional).
	 * @return {boolean} - Returns true if the element was highlighted, false otherwise.
	 * @example
	 * _Pict.providers.UtilityProvider.highlightListItemRowElement(document.querySelector('.list-item-row[data-obseravtion-id="123456"]'));
	 */
	highlightListItemRowElement(pListItemRowElement, pScrollConainerElement)
	{
		if (!pListItemRowElement)
		{
			console.warn('[Utility] Unable to highlight list item row element, it is null or undefined.');
			return false;
		}
		const tmpListItemRowElement = pListItemRowElement instanceof HTMLElement ? pListItemRowElement : document.querySelector(pListItemRowElement);
		if (tmpListItemRowElement)
		{
			tmpListItemRowElement.classList.add('is-selected', 'animate__animated', 'animate__pulse');
			// NOTE: Do not do "tmpListItemRowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });" bc it scrolls the whole page, not just the list item row
			if (this.pict.ContentAssignment.hasJquery)
			{
				// if we have a scroll container element, use it to scroll the list item into view
				if (pScrollConainerElement && $(pScrollConainerElement).length > 0)
				{
					$(pScrollConainerElement).animate({
						scrollTop: $(tmpListItemRowElement).offset().top - $(pScrollConainerElement).offset().top + $(pScrollConainerElement).scrollTop(),
					}, 500);
				}
			}
			setTimeout(() =>
			{
				tmpListItemRowElement.classList.remove('is-selected', 'animate__animated', 'animate__pulse');
			}
			, 5000); // 5 seconds
			return true;
		}
		else
		{
			console.warn('[Utility] Unable to highlight list item row element, it is null or undefined.');
			return false;
		}
	}

	/**
	 * This method is used to remove the loading skeletons from an element. It tries to be smart about where he classes are on the parent element and the element itself.
	 * @param {HTMLElement|jQuery} pElement - The element to remove the loading skeletons from.
	 * @return {boolean} - Returns true if the skeletons were removed, false otherwise.
	 */
	removeLoadingSkeleton(pElement)
	{
		if (!pElement)
		{
			console.warn('[Utility] Unable to remove loading skeletons, the element is null or undefined.');
			return false;
		}
		// if the element is a jQuery object, get the first element
		if (pElement instanceof jQuery)
		{
			pElement = pElement[0];
		}
		// if the element is not an HTMLElement, return false
		if (!(pElement instanceof HTMLElement))
		{
			console.warn('[Utility] Unable to remove loading skeletons, the element is not an HTMLElement.');
			return false;
		}
		// if the parent element has the 'is-skeleton' class, remove it there
		if (pElement?.parentElement?.classList?.contains('is-skeleton'))
		{
			pElement.parentElement.classList.remove('is-skeleton');
			return true;
		}
		// if the element itself has the 'is-skeleton' class, remove it there
		else if (pElement?.classList?.contains('is-skeleton'))
		{
			pElement.classList.remove('is-skeleton');
			return true;
		}
		else
		{
			console.warn('[Utility] Unable to remove loading skeletons, the element does not have the is-skeleton class.');
			return false;
		}
	}

	/**
	 * A general utility to pause any active media on the page.
	 * @return {void}
	 */
	pauseAnyActiveMedia()
	{
		const tmpMedia = document.querySelectorAll('video, audio');
		tmpMedia.forEach(
			/**
			 * @param {HTMLMediaElement|HTMLVideoElement} media
			 */
			(media) =>
			{
				if (!media.paused)
				{
					media.pause();
				}
			});
	}

	/**
	 * This method returns the subdomain based on the current environment.
	 * It checks if the URL contains 'localhost' or '.qa.' for QA, '.stg.' for staging, and returns an empty string for production.
	 * @return {string} - The subdomain for the current environment.
	 */
	getEnvironmentSubdomainFromWindowLocation()
	{
		if (window?.location?.href?.includes('localhost') || window?.location?.href?.includes('.qa.'))
		{
			return '.qa';
		}
		else if (window?.location?.href?.includes('.stg.'))
		{
			return '.stg';
		}
		else
		{
			return '';
		}
	};

	/**
	 * This method builds the links URL based on the current environment.
	 * It uses the getEnvironmentSubdomainFromWindowLocation method to determine the subdomain.
	 * @return {string} - The links URL for the current environment.
	 */
	buildLinksUrl()
	{
		const subdomain = this.getEnvironmentSubdomainFromWindowLocation();
		return `https://links${ subdomain }.headlight.com`;
	}
}

module.exports = UtilityProvider;
module.exports.default_configuration = {};
